(() => {
  'use strict';

  const root = document.querySelector('.t4s-main-cart');
  const form = root?.querySelector('[data-cart-wrapper]');
  if (!root || !form || form.dataset.ktCartPageInitialized === 'true') return;
  form.dataset.ktCartPageInitialized = 'true';

  // Capture the native fetch up-front so later wrappers (analytics, etc.)
  // never interfere with our own requests.
  const nativeFetch = window.fetch.bind(window);

  let scheduled = false;
  let refreshTimer;
  let lastCartSignature;
  let cartObserver;
  let unreadableTimer;
  let emptyCheckInFlight = false;

  const OBSERVE_OPTS = { childList: true, subtree: true, characterData: true };
  const observeForm = () => cartObserver && cartObserver.observe(form, OBSERVE_OPTS);

  /**
   * KT-219: run our own DOM writes WITHOUT the observer hearing them.
   *
   * `syncCartLabels` writes to the DOM (the is-empty class, the heading count, the checkout
   * label) and the observer below watches childList + subtree + characterData. So every sync
   * scheduled the next one: the module was listening to itself. `scheduled` collapsed each
   * batch into one rAF, which hid the churn rather than stopping it.
   *
   * disconnect -> write -> takeRecords() -> reconnect. takeRecords is the load-bearing part:
   * it drains records queued before the disconnect, so our own writes cannot survive into the
   * next callback.
   */
  const applyOwnWrites = (write) => {
    if (cartObserver) cartObserver.disconnect();
    try {
      write();
    } finally {
      if (cartObserver) {
        cartObserver.takeRecords();
        observeForm();
      }
    }
  };

  /**
   * KT-219: emptiness is decided by Shopify, never by a DOM count.
   *
   * `is-empty` used to be set from `[data-cart-item] [data-quantity-value]` returning zero
   * matches. That conflates "the cart has no items" with "the item list is not in the DOM
   * right now" — and the theme replaces the item list wholesale on every section render, so
   * there is a window where the count is legitimately 0 for a cart that is not empty.
   *
   * When that window is observed, `is-empty` hides the whole order-summary panel: the
   * subtotal, the discount line and the checkout button disappear and the footer jumps up the
   * page. It comes back on the next render. Repeated, that reads exactly as "the cart keeps
   * refreshing" — reported 2026-08-17 with a screen recording showing the summary panel
   * alternating in and out roughly every second while the URL never changed.
   *
   * So a flip TO empty now costs one authoritative /cart.js read. Flipping AWAY from empty
   * stays synchronous: a DOM that shows items is proof enough that the cart is not empty.
   */
  const confirmEmptyWithShopify = async () => {
    if (emptyCheckInFlight) return;
    emptyCheckInFlight = true;
    try {
      const cart = await nativeFetch('/cart.js').then((r) => r.json());
      if (cart && cart.item_count === 0) {
        applyOwnWrites(() => form.classList.add('is-empty'));
      }
    } catch (error) {
      // Leave the DOM exactly as it is. Guessing "empty" on a failed request is the bug.
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Cart empty-state check failed:', error);
    } finally {
      emptyCheckInFlight = false;
    }
  };

  /**
   * KT-228: the heading count, written from ONE place, by whichever caller has the truth.
   *
   * `[data-cart-heading-count]` lives in snippets/heading_page.liquid, rendered by
   * sections/heading-template.liquid — a DIFFERENT section from main-cart. So a cart-section
   * re-render never touches it: it keeps its server-rendered text until this module writes it.
   *
   * That matters because every trigger for syncCartLabels is either a DOM mutation INSIDE the
   * cart form, an `input` event, or cart:updated / cart:refresh. Setting `input.value`
   * programmatically produces NO mutation record and fires NO input event. So if the theme
   * updates quantities in place instead of replacing the form, and signals it with an event
   * name this module does not listen for, syncCartLabels never runs — and the heading sits at
   * its server-rendered value while the badge and the server move on.
   *
   * Reported as heading "5 items" beside badge 6 and /cart.js 6, on a 2-line cart with
   * discounts, holding for 8 seconds. Note the reported mechanism (this module caching a
   * detached node) is NOT what happens — the element is re-queried on every write, below.
   * The fault is that nothing re-ran the write at all.
   *
   * KT-226's re-arm cannot help: it only fires once a sync has RUN and found blanks.
   */
  const writeItemCount = (count) => {
    if (!Number.isFinite(count)) return;
    const node = document.querySelector('[data-cart-heading-count]');
    if (!node) return;
    const label = count === 1 ? node.dataset.labelOne : node.dataset.labelOther;
    const nextText = `${count} ${label}`;
    if (node.textContent.trim() !== nextText) node.textContent = nextText;
  };

  const writeGlobalCartState = (cart) => {
    if (!cart || !Number.isFinite(cart.item_count)) return;
    writeItemCount(cart.item_count);
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
    });
    let totalText;
    if (window.T4SThemeSP?.Currency?.formatMoney) {
      totalText = window.T4SThemeSP.Currency.formatMoney(cart.total_price);
    } else {
      totalText = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: cart.currency || 'GBP'
      }).format(cart.total_price / 100);
    }
    document.querySelectorAll('[data-cart-tt-price]').forEach((node) => {
      node.textContent = totalText;
    });
  };

  const replaceOrRemove = (current, next) => {
    if (current && next) current.replaceWith(next);
    else if (current && !next) current.remove();
  };

  const fetchSectionDocument = async () => {
    const sectionId = form.dataset.sectionId;
    if (!sectionId) return null;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('section_id', sectionId);
    const response = await nativeFetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Cart section request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const refreshLiquidPromises = async () => {
    try {
      const nextDocument = await fetchSectionDocument();
      if (!nextDocument) return;
      replaceOrRemove(
        form.querySelector('[data-free-shipping-bar]'),
        nextDocument.querySelector('[data-free-shipping-bar]')
      );
      replaceOrRemove(
        form.querySelector('.kt-cart-bulk-pricing'),
        nextDocument.querySelector('.kt-cart-bulk-pricing')
      );
    } catch (error) {
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Could not refresh cart promises:', error);
    }
  };

  // ---------------------------------------------------------------------------
  // Quantity commit.
  // The theme sends one /cart/change.js request per click using a line key that rotates
  // after every mutation. Rapid clicks therefore race and later requests can silently
  // lose. The capture-phase handler below owns +/- clicks, reflects each intent locally,
  // debounces them, then commits every pending line together with fresh keys from /cart.js.
  // Manual input changes may still be handled by the theme; this code stands down when
  // Shopify already shows the intended quantity.
  // ---------------------------------------------------------------------------
  let verifyTimer;
  let committing = false;
  const pendingTargets = new Map();

  /**
   * Team feedback 2026-08-21: the theme's own change handler re-renders the blocks IT
   * knows about ([data-cart-prices], the item list) but not the KT summary rows, so a
   * theme-applied quantity change could leave "Items subtotal" and the delivery total
   * showing the PREVIOUS cart state — reported live as items subtotal £22.46 beside
   * lines summing £28.54, self-healing on reload. The section stamps the cart
   * fingerprint it was rendered from on [data-kt-cart-state]; when the authoritative
   * /cart.js disagrees, the whole section is re-rendered. Absent node = empty cart or
   * pre-update theme = nothing to reconcile.
   */
  const summaryIsStale = (cart) => {
    const node = form.querySelector('[data-kt-cart-state]');
    if (!node || !cart) return false;
    return node.dataset.ktCartState !== `${cart.item_count}:${cart.total_price}`;
  };

  let reconcileTimer;
  let reconciling = false;
  const reconcileSummary = async () => {
    if (reconciling || committing) return;
    reconciling = true;
    try {
      const cart = await nativeFetch('/cart.js').then((r) => r.json());
      if (summaryIsStale(cart)) await rerenderCartBody();
    } catch (error) {
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Cart summary reconcile failed:', error);
    } finally {
      reconciling = false;
    }
  };

  const rerenderCartBody = async () => {
    const nextDocument = await fetchSectionDocument();
    if (!nextDocument) return;
    const nextWrapper = nextDocument.querySelector('[data-cart-wrapper]');
    if (!nextWrapper) return;
    form.innerHTML = nextWrapper.innerHTML;
    form.classList.toggle('is-empty', nextWrapper.classList.contains('is-empty'));
  };

  // Shopify keeps code discounts in the cart session. Let the shopper remove a
  // code explicitly while leaving automatic discounts (including multibuy) alone.
  form.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-remove-discount-code]');
    if (!button || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const updateResponse = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: button.dataset.remainingDiscountCodes || '' })
      });
      if (!updateResponse.ok) throw new Error(`Discount update failed: ${updateResponse.status}`);
      await rerenderCartBody();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Could not remove discount code:', error);
    }
  });

  const schedulePendingVerify = (delay = 1200) => {
    window.clearTimeout(verifyTimer);
    verifyTimer = window.setTimeout(verifyPendingTargets, delay);
  };

  const clearAppliedIntents = (intents) => {
    intents.forEach((target, lineIdentity) => {
      if (pendingTargets.get(lineIdentity) === target) pendingTargets.delete(lineIdentity);
    });
  };

  async function verifyPendingTargets() {
    if (!pendingTargets.size) return;
    if (committing) {
      schedulePendingVerify(250);
      return;
    }
    committing = true;
    const intents = new Map(pendingTargets);
    try {
      const cart = await nativeFetch('/cart.js').then((r) => r.json());
      const updates = {};
      intents.forEach((target, lineIdentity) => {
        const variantId = String(lineIdentity).split(':')[0];
        const exactLine = cart.items.find((item) => String(item.key) === String(lineIdentity));
        const variantLines = cart.items.filter((item) => String(item.variant_id) === variantId);
        // Prefer the exact line key. If another mutation rotated it, variant ID is safe only
        // when it identifies one line; duplicate variants with different custom properties
        // must never be collapsed into or applied to the wrong line.
        const line = exactLine || (variantLines.length === 1 ? variantLines[0] : null);
        if (line && line.quantity !== target) updates[line.key] = target;
      });

      if (!Object.keys(updates).length) {
        // The theme or a manual input path already applied every target. Reconcile all global
        // cart labels from this authoritative read; no extra request or polling is needed.
        applyOwnWrites(() => writeGlobalCartState(cart));
        if (summaryIsStale(cart)) await rerenderCartBody();
        clearAppliedIntents(intents);
        return;
      }
      // Rapid clicks can overlap the theme's own /cart/change.js requests. Those requests
      // reuse a line key that rotates after the first mutation, so a later click can be
      // silently lost. Apply all still-missing intents in one update using keys from this
      // fresh cart read; this is also safe when two different lines were changed quickly.
      const updateResponse = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!updateResponse.ok) throw new Error(`Cart fallback update failed: ${updateResponse.status}`);
      const updatedCart = await updateResponse.json();
      applyOwnWrites(() => writeGlobalCartState(updatedCart));
      await rerenderCartBody();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      clearAppliedIntents(intents);
    } catch (error) {
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Cart fallback commit failed:', error);
      clearAppliedIntents(intents);
      try {
        await rerenderCartBody();
      } catch (renderError) {
        if (window.KT?.utils?.log) window.KT.utils.log('error', 'Cart rollback render failed:', renderError);
      }
    } finally {
      committing = false;
      if (pendingTargets.size) schedulePendingVerify(300);
    }
  }

  const scheduleVerify = (lineIdentity, target, delay = 1200) => {
    if (!lineIdentity || !Number.isFinite(target) || target < 0) return;
    pendingTargets.set(lineIdentity, target);
    schedulePendingVerify(delay);
  };

  const intentFromInput = (input) => {
    if (!input) return;
    const lineIdentity = String(input.dataset.id || '');
    scheduleVerify(lineIdentity, Number.parseInt(input.value, 10));
  };

  const intendedStepTarget = (input, direction) => {
    if (!input) return null;
    const lineIdentity = String(input.dataset.id || '');
    if (!lineIdentity) return null;
    // Password/autofill extensions have been observed blanking the live value
    // property of Shopify's numeric quantity input while leaving its rendered
    // value attribute intact. Treat that attribute as the server-state fallback
    // so +/- remains usable instead of silently doing nothing for those shoppers.
    const liveValue = Number.parseInt(input.value, 10);
    const renderedValue = Number.parseInt(input.getAttribute('value'), 10);
    const current = Number.isFinite(liveValue) ? liveValue : renderedValue;
    if (!Number.isFinite(current)) return null;
    const base = pendingTargets.has(lineIdentity) ? pendingTargets.get(lineIdentity) : current;
    const step = Number.parseInt(input.getAttribute('step'), 10) || 1;
    const min = Number.parseInt(input.getAttribute('min'), 10);
    const max = Number.parseInt(input.getAttribute('max'), 10);
    let target = base + direction * step;
    if (Number.isFinite(min)) target = Math.max(min, target);
    if (Number.isFinite(max)) target = Math.min(max, target);
    input.value = String(target);
    scheduleVerify(lineIdentity, target, 320);
    return true;
  };

  // Capture before the theme's delegated click handler. Owning this one interaction prevents
  // concurrent stale-key requests; the input is updated synchronously so the control still feels
  // immediate, while keyboard-generated clicks follow the identical accessible path.
  form.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-increase-qty], [data-decrease-qty]');
    if (!btn) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const input = btn.closest('[data-cart-item]')?.querySelector('[data-quantity-value]');
    const direction = btn.hasAttribute('data-increase-qty') ? 1 : -1;
    if (intendedStepTarget(input, direction)) scheduleSync();
  }, true);

  /**
   * Removal must go through the same pending-intents pipeline as +/-, for the
   * same reason quantity clicks do: the theme's remove anchor posts
   * /cart/change.js with the line key rendered in the DOM, and a key that has
   * rotated after any earlier mutation is silently ignored by Shopify
   * (HTTP 200, cart unchanged). Reproduced on production 2026-08-27: step a
   * quantity, click the line's bin icon — change.js returned 200 and the item
   * survived. verifyPendingTargets resolves a fresh key from /cart.js before
   * committing, so removal cannot be lost to key rotation. A duplicate-variant
   * line (same variant, different properties) is only ever matched by exact
   * key — identical to the quantity path, never collapsed onto the wrong line.
   * The anchor's native href (/cart/change?quantity=0&id=…) remains the
   * no-JS fallback.
   */
  form.addEventListener('click', (event) => {
    const remover = event.target.closest('[data-cart-remove]');
    if (!remover) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const item = remover.closest('[data-cart-item]');
    const lineIdentity = String(
      remover.dataset.id || item?.querySelector('[data-quantity-value]')?.dataset.id || ''
    );
    if (!lineIdentity) return;
    remover.setAttribute('aria-busy', 'true');
    if (item) item.classList.add('is-removing');
    scheduleVerify(lineIdentity, 0, 120);
  }, true);
  // Manual typing.
  form.addEventListener('change', (event) => {
    if (event.target.matches('[data-quantity-value]')) intentFromInput(event.target);
  });

  // ---------------------------------------------------------------------------
  // Passive label sync (read-only) — reflects quantity edits into the heading
  // count and checkout button label immediately, and refreshes the promo blocks
  // once the DOM settles. Never writes to the cart.
  // ---------------------------------------------------------------------------
  /**
   * KT-220: a BLANK quantity means "the theme is mid-re-render", not "zero".
   *
   * The theme replaces cart fragments in place, and there is a window where a
   * `[data-quantity-value]` exists with an empty value. Everything below used to read
   * that as 0, which produced the reported cart-refresh loop:
   *
   *   blank value -> itemCount 0 -> heading written as "0 items" WHILE a line item is
   *   visibly on screen (seen in the 2026-08-17 screen recording: header badge "1", a
   *   Corby kettle rendered, heading "0 items", quantity box empty)
   *   -> confirmEmptyWithShopify() fires a request
   *   -> signature below embeds the raw value, so it flips "id:1" <-> "id:"
   *   -> the flip schedules refreshLiquidPromises 600ms later
   *   -> that replaces DOM inside `form`, the observer hears it, sync runs again,
   *      and the quantity flips back -> another flip, another 600ms refresh, forever.
   *
   * The loop is fed by a DISAGREEMENT, not by a missing guard — the KT-219
   * disconnect/takeRecords guard is intact and still correct; it just cannot help when
   * each pass legitimately observes a different value.
   *
   * KT-222 — the heading write above is REAL, because that was challenged.
   * `[data-cart-heading-count]` lives in snippets/heading_page.liquid:31, rendered via
   * sections/heading-template.liquid. A review claimed it "exists in no Liquid file, only in
   * the JS that queries it", and therefore that this explanation credited a mechanism that
   * does not exist. It does, and the recording proves it: sampled at 1fps the heading read
   *   1 item · 0 items · 1 item · 0 items · 1 item · 0 items · 1 item · … · 1 item
   * — exactly this write, with singular/plural from data-label-one / data-label-other.
   * test/cart-savings-attribution.test.js pins the element so the claim cannot be re-raised
   * from a grep of the wrong directories.
   *
   * So an unreadable quantity now yields `null` here and an early return below: no
   * count is written, no empty-check fires, and the signature is not comparable, so
   * nothing is scheduled. The next sync — after the DOM settles — sees real numbers.
   */
  /**
   * A quantity input can be left with an EMPTY live value after the theme
   * replaces the item list mid-interaction (reproduced on production
   * 2026-08-27 after a failed stale-key removal; also reported with autofill
   * extensions blanking the value property). Everywhere else a blank value
   * correctly means "mid-re-render", so if it never settles the row is stuck:
   * steppers still work via the rendered attribute, but typing, the heading
   * count and the empty-check all stay parked. Repair is one authoritative
   * /cart.js read that restores each blank input's real quantity by exact line
   * key. Never invents a number: an unmatched (rotated) key is left for
   * reconcileSummary's full section re-render instead.
   */
  let repairingBlanks = false;
  const repairBlankQuantities = async () => {
    if (repairingBlanks || committing) return;
    const blanks = [...form.querySelectorAll('[data-cart-item] [data-quantity-value]')]
      .filter((input) => !String(input.value).trim());
    if (!blanks.length) return;
    repairingBlanks = true;
    try {
      const cart = await nativeFetch('/cart.js').then((r) => r.json());
      applyOwnWrites(() => {
        blanks.forEach((input) => {
          if (String(input.value).trim()) return;
          const line = cart.items.find((item) => String(item.key) === String(input.dataset.id));
          if (line) input.value = String(line.quantity);
        });
      });
      if (summaryIsStale(cart)) await rerenderCartBody();
      scheduleSync();
    } catch (error) {
      if (window.KT?.utils?.log) window.KT.utils.log('error', 'Cart blank-quantity repair failed:', error);
    } finally {
      repairingBlanks = false;
    }
  };

  const readQuantities = () => {
    const inputs = [...form.querySelectorAll('[data-cart-item] [data-quantity-value]')];
    if (inputs.some((input) => !String(input.value).trim())) return null;
    return inputs;
  };

  const getCartSignature = () => {
    const inputs = readQuantities();
    if (!inputs) return null;
    return inputs.map((input) => `${input.dataset.id}:${input.value}`).sort().join('|');
  };

  const syncCartLabels = () => {
    scheduled = false;

    const quantityInputs = readQuantities();
    if (!quantityInputs) {
      // Mid-re-render. Write nothing; see KT-220 above.
      //
      // KT-226: but DO come back. Returning without rescheduling assumed something else
      // would re-run this once the DOM settled, and today something does — the theme
      // replaces the item list wholesale, and that childList mutation reaches the observer.
      // Verified on production: after one and after two rapid "+" clicks, the quantity,
      // heading, badge and /cart.js all agreed within a second and stayed agreed for 9s.
      //
      // It is covered incidentally, though, not by design. An input's `value` property
      // changing produces NO mutation record and fires no `input` event when set
      // programmatically, so if the theme ever patched quantities in place instead of
      // replacing nodes, this early return would strand the heading at a stale number
      // permanently — "unknown" quietly becoming "never updated". One bounded re-check
      // closes that without reintroducing a timer loop: it is cancelled by any later sync,
      // and it writes nothing itself.
      window.clearTimeout(unreadableTimer);
      unreadableTimer = window.setTimeout(() => {
        scheduleSync();
        // Still blank two passes later means nothing is coming to repair it —
        // the theme render that normally settles this has already happened.
        window.setTimeout(() => { repairBlankQuantities(); }, 400);
      }, 400);
      return;
    }
    window.clearTimeout(unreadableTimer);
    const itemCount = quantityInputs.reduce((total, input) => {
      const quantity = Number.parseInt(input.value, 10);
      return total + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);

    // The theme replaces cart fragments after a remove. In particular, the
    // last-item transition can replace the item list and subtotal without
    // replacing the form class, heading, or checkout label. Keep the empty
    // state authoritative so stale checkout UI cannot remain actionable.
    //
    // KT-219: "authoritative" now means Shopify, not this DOM count — see
    // confirmEmptyWithShopify. Showing items is proof of non-empty, so that direction is
    // immediate; claiming empty requires confirmation.
    if (itemCount > 0) {
      if (form.classList.contains('is-empty')) {
        applyOwnWrites(() => form.classList.remove('is-empty'));
      }
    } else if (!form.classList.contains('is-empty')) {
      confirmEmptyWithShopify();
    }

    applyOwnWrites(() => {
      writeItemCount(itemCount);

      const deliveryTotal = form.querySelector('[data-kt-delivery-total]:not([hidden]) [data-kt-delivery-total-value]');
      const checkoutLabel = form.querySelector('[data-cart-checkout-label]');
      const checkoutButton = checkoutLabel?.closest('[name="checkout"]');
      // Liquid owns the blocked label for an unavailable cart line. Replacing it with
      // "Checkout · £…" would contradict the disabled control and hide the action the
      // customer must take. A section re-render restores the normal label once every
      // remaining line is purchasable.
      if (checkoutLabel && !checkoutButton?.disabled) {
        const nextText = deliveryTotal?.textContent.trim()
          ? `${checkoutLabel.dataset.label} · ${deliveryTotal.textContent.trim()}`
          : checkoutLabel.dataset.label;
        if (checkoutLabel.textContent.trim() !== nextText) checkoutLabel.textContent = nextText;
      }
    });

    // KT-220: null = quantities unreadable mid-re-render. Neither adopt it nor compare
    // against it — adopting would make the NEXT real value look like a change and schedule a
    // refresh, which is the loop this fix removes.
    //
    // Unreachable from here by construction, because syncCartLabels already returned above
    // when readQuantities() was null and nothing awaits in between. Kept as a contract on
    // getCartSignature rather than an assumption about its one caller: if a second call site
    // ever appears, the null case is already handled instead of silently comparing
    // "null" !== "id:1" and scheduling a refresh.
    const nextSignature = getCartSignature();
    if (nextSignature !== null) {
      if (lastCartSignature === undefined) {
        lastCartSignature = nextSignature;
      } else if (nextSignature !== lastCartSignature) {
        lastCartSignature = nextSignature;
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(refreshLiquidPromises, 600);
        // The cart composition changed under the theme's control: verify the KT summary
        // rows against the authoritative cart once the dust settles. One read; no-op
        // when the fingerprint matches (the overwhelmingly common case).
        window.clearTimeout(reconcileTimer);
        reconcileTimer = window.setTimeout(reconcileSummary, 900);
      }
    }
  };

  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(syncCartLabels);
  };

  form.addEventListener('input', scheduleSync);
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);

  // Native theme section rendering does not consistently emit its cart event
  // after the replacement has landed. Observe the form as a passive fallback;
  // scheduleSync is idempotent and never writes to Shopify cart state.
  cartObserver = new MutationObserver(scheduleSync);
  observeForm();
  syncCartLabels();
})();
